/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2020
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import QuerySubjectUtils from './QuerySubjectUtils';
import DataModuleExtractor from './DataModuleExtractor';
import ModuleUtils from './ModuleUtils';
import ReferenceResolver from './ReferenceResolver';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/client/utils/QuerySubjectUtils (runtime) // getParentOfType
//@import com/ibm/bi/platform/moser/core/module/util/DataModuleExtractor (runtime) // getUseSpec
//@import com/ibm/bi/platform/moser/client/utils/ModuleUtils (runtime) // findObjectTypeByIdForExpression
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (static) // getSecondPart

/**
 * @author  Natalia Balaba
 */
export default class ObjectNavigator extends BaseMoserObject{

	module = null;

	catalog = null;



	/**
	 * @param {Module} module
	 * @param {Map} catalog
	 */
	constructor(module, catalog) {
		super();
		this.module = module;
		this.catalog = catalog;
	}

	/**
	 * @param {ObjectType} baseModuleObject - an object in a base module
	 * @param {String} baseModuleUseSpecID - id of the use spec (e.g. M1, M2) used to describe the base module containing source object
	 * @return {MoserObject}  an object from a business module created using source object
	 */
	findModuleObjectUsingBaseObject(baseModuleObject, baseModuleUseSpecID) {
		if (!baseModuleObject || !(this.catalog.hasOwnProperty(baseModuleUseSpecID))) {
			return null;
		}
		if ("QuerySubject" == baseModuleObject.getObjectType()) {
			var ref = baseModuleUseSpecID + "." + baseModuleObject.getIdentifier();
			for (var __i_enFor0 = 0, __exp_enFor0 = this.module.getQuerySubject(), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qs = __exp_enFor0[__i_enFor0];
				if (!(qs.getOriginalRef().length==0)) {
					if (ref == qs.getOriginalRef()[0]) {
						return qs;
					}
				} else if (!(qs.getRef().length==0) && ref == qs.getRef()[0]) {
					return qs;
				}
			}
		} else if ("Relationship" == baseModuleObject.getObjectType()) {
			return null;
		} else if ("Filter" == baseModuleObject.getObjectType() || "Calculation" == baseModuleObject.getObjectType() || "QueryItem" == baseModuleObject.getObjectType()) {
			var querySubject = QuerySubjectUtils.getParentOfType("QuerySubject", baseModuleObject);
			if (querySubject) {
				var idForExpression = baseModuleUseSpecID + "." + querySubject.getIdentifier();
				for (var __i_enFor1 = 0, __exp_enFor1 = this.module.getQuerySubject(), __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var qs = __exp_enFor1[__i_enFor1];
					if (!(qs.getOriginalRef().length==0)) {
						var origRef = qs.getOriginalRef()[0];
						if (idForExpression == origRef) {
							return this._locateModuleItemUsingBase(baseModuleObject, qs.getItem());
						}
					}
					if (!(qs.getRef().length==0)) {
						var ref = qs.getRef()[0];
						if (idForExpression == ref) {
							return this._locateModuleItemUsingBase(baseModuleObject, qs.getItem());
						}
					}
				}
			}
		}
		return null;
	}

	/**
	 * @param {ObjectType} baseItem - item (a query subject child) from a base module
	 * @param {Array} items - items collection to search
	 * @return {MoserObject}  target item in the business module using source item
	 */
	_locateModuleItemUsingBase(baseItem, items) {
		for (var __i_enFor0 = 0, __exp_enFor0 = items, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			if (item.getFolder()) {
				var object = this._locateModuleItemUsingBase(baseItem, item.getFolder().getItem());
				if (object) {
					return object;
				}
			}
			if ("Filter" == baseItem.getObjectType()) {
				if (item.getFilter() && item.getFilter().getIdentifier() == baseItem.getIdentifier()) {
					return item.getFilter();
				}
			} else if ("Calculation" == baseItem.getObjectType()) {
				if (item.getCalculation() && item.getCalculation().getIdentifier() == baseItem.getIdentifier()) {
					return item.getCalculation();
				}
			} else if ("QueryItem" == baseItem.getObjectType()) {
				if (item.getQueryItem() && item.getQueryItem().getIdentifier() == baseItem.getIdentifier()) {
					return item.getQueryItem();
				}
			}
		}
		return null;
	}

	/**
	 * @param {ObjectType} moduleObject - an object inside business module
	 * @return {MoserObject}  original object from a base module target object was created from
	 */
	findBaseObjectUsingModuleObject(moduleObject) {
		if (!moduleObject) {
			return null;
		}
		if ("QuerySubject" == moduleObject.getObjectType()) {
			var useSpec = DataModuleExtractor.getUseSpec(moduleObject, this.module, true);
			if (!useSpec) {
				return null;
			}
			var base = this.catalog[useSpec.getIdentifier()];
			return ModuleUtils.findObjectTypeByIdForExpression(base, ObjectNavigator._getSecondPart(moduleObject));
		} else if ("Relationship" == moduleObject.getObjectType()) {
			return null;
		} else if ("Filter" == moduleObject.getObjectType() || "Calculation" == moduleObject.getObjectType() || "QueryItem" == moduleObject.getObjectType()) {
			var querySubject = QuerySubjectUtils.getParentOfType("QuerySubject", moduleObject);
			if (querySubject) {
				return this._locateBaseItemUsing(moduleObject, querySubject);
			}
		}
		return null;
	}

	/**
	 * @param {ObjectType} moduleItem a child of a query subject in the business module
	 * @param {QuerySubject} moduleQuerySubject a parent of the moduleItem from the business module
	 * @return {MoserObject}  a base module item from a base module used to create moduleItem
	 */
	_locateBaseItemUsing(moduleItem, moduleQuerySubject) {
		var useSpec = this._getUseSpec(moduleQuerySubject);
		if (!useSpec) {
			return null;
		}
		var base = this.catalog[useSpec.getIdentifier()];
		var qsRef = ObjectNavigator._getSecondPart(moduleQuerySubject);
		var qiRef = ReferenceResolver.getSecondPart(moduleItem.getIdForExpression());
		var idForExpression = qsRef + "." + qiRef;
		return ModuleUtils.findObjectTypeByIdForExpression(base, idForExpression);
	}

	/**
	 * @param {ObjectType} object
	 * @return {UseSpec}
	 */
	_getUseSpec(object) {
		return DataModuleExtractor.getUseSpec(object, this.module, true);
	}

	/**
	 * @param {QuerySubject} querySubject
	 * @return {String}
	 */
	static _getSecondPart(querySubject) {
		if (!(querySubject.getOriginalRef().length==0)) {
			return ReferenceResolver.getSecondPart(querySubject.getOriginalRef()[0]);
		}
		return ReferenceResolver.getSecondPart(querySubject.getRef()[0]);
	}
}


// WEBPACK FOOTER //
// ./src/generated/ObjectNavigator.js